/* @flow */
import React, { PureComponent } from 'react';
import { type TFunction } from 'react-i18next';

import { lazy } from 'common/modules/mfe';
import { usePlatformSettings } from 'common/modules/platform-settings';
import SettingsChangePassword from 'platform/common/components/changePassword/SettingsChangePassword';

import GeneralSettings from './components/GeneralSettings';
import NotificationSettings from './components/Notification';
import PersonnelLocaleSettings from './components/PersonnelLocaleSettings';
import ClientConfigService from 'common/services/ClientConfigService';

import { PlatformSettings } from 'common/modules/platform-settings/models/internal/PlatformSettings';

import theme from './MySettingsContainer.scss';

const AlertsPreference = lazy(() =>
    import('platform-app/alerts/AlertsPreference')
);

type CardProps = {
    title: string,
    children: React$Node
};

class SettingsCard extends PureComponent<CardProps> {
    render() {
        const { title, children } = this.props;
        return (
            <div className={theme.settingsCardContainer}>
                <div className={theme.cardTitle}>{title}</div>
                <div className={theme.cardBox}>{children}</div>
            </div>
        );
    }
}

type Props = {
    settings: PlatformSettings,
    t: TFunction
};

export class MySettingsContainer extends PureComponent<Props> {
    getComponentConfig() {
        const { t, settings, ...rest } = this.props;
        return {
            general: {
                name: t('general'),
                component: <GeneralSettings {...rest} />
            },
            notification: {
                name: t('notification_plural'),
                component:
                    ClientConfigService.isAlertsEnabled() &&
                    settings.alertsSettings?.isAlertsEnabled ? (
                        <AlertsPreference />
                    ) : (
                        <NotificationSettings {...rest} />
                    )
            },
            locale: {
                name: t('localeSettings'),
                component: <PersonnelLocaleSettings />
            },
            security: {
                name: t('security'),
                component: <SettingsChangePassword />
            }
        };
    }

    render() {
        const componentConfig = this.getComponentConfig();
        return (
            <div className={theme.settingsContainer}>
                {Object.keys(componentConfig).map(section => {
                    const { name, component } = componentConfig[section];
                    return (
                        <SettingsCard title={name} key={section}>
                            {component}
                        </SettingsCard>
                    );
                })}
            </div>
        );
    }
}

const withSettings = (Component: React$ComponentType<any>) => {
    return props => {
        const settings = usePlatformSettings();
        return <Component {...props} settings={settings} />;
    };
};

export default withSettings(MySettingsContainer);
